import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { StatusCode } from "api/protocols";
import Page from "components/page/Page";
import DataTable from "components/dataTable/DataTable";
import Card from "components/card/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/pro-regular-svg-icons";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import Tabs from "components/tabs/Tabs";
import "./ratePlans.scss";
import Toggle from "components/form/toggle/Toggle";
import RatePlanTable, { IUnit } from "components/ratePlanTable/RatePlanTable";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select/index";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import { formatDate } from "helpers/Helpers";
import { IActionDetails } from "components/page/PageActions";
import Popup from "components/popup/Popup";
import Portal from "elements/Portal";
import { useHistory, useParams } from "react-router-dom";
import { IAccommodationUnitType } from "../unitTypes/UnitTypes";
import {
  GetAccommodationRatePlans,
  PostAccommodationRatePlan,
} from "api/rpc/2024-04/facilityAdmin/accommodation/ratePlans/ratePlan";
import { GetAccommodationUnitTypes } from "api/rpc/2024-04/facilityAdmin/accommodation/unitTypes";
import {
  GetAccommodationRatePlanPrices,
  IAccommodationRatePlanPrice,
  IPutAccommodationRatePlanPrice,
  PutAccommodationRatePlanPrice,
} from "api/rpc/2024-04/facilityAdmin/accommodation/ratePlans/ratePlanPrices";

export interface IAccommodationRatePlan {
  id?: number;
  title: string;
  code: string;
  public: boolean;
  days_valid: string;
  base_price: string;
}

export interface IGroupedRatePlanPrices {
  rate_plan_id: number;
  rate_plan_name: string;
  units: {
    unit_type_id: number;
    prices: IAccommodationRatePlanPrice[];
    close_on_arrival?: boolean;
    close_on_departure?: boolean;
    id?: number;
  }[];
}

export function RatePlans() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { tab } = useParams<{ tab?: string }>();
  const [selected, setSelected] = useState<number>(0);
  const history = useHistory();

  const [accommodationRatePlans, setAccommodationRatePlans] = useState<IAccommodationRatePlan[]>(undefined);
  const [accommodationUnitTypes, setAccommodationUnitTypes] = useState<IAccommodationUnitType[]>(undefined);
  const [accommodationRatePlanPrices, setAccommodationRatePlanPrices] = useState<IGroupedRatePlanPrices[]>([]);

  const [saveStatus, setSaveStatus] = useState<"saving" | "saved" | null>(null);

  const [newRatePlanState, setNewRatePlanState] = useState<IAccommodationRatePlan & { open: boolean }>({
    title: "",
    code: "",
    public: false,
    open: false,
    days_valid: undefined,
    base_price: undefined,
  });

  const [filterState, setFilterState] = useState({
    search: "",
    offset: 0,
    limit: 10,
    startingDate: new Date(),
    endingDate: new Date(new Date().setDate(new Date().getDate() + 6)),
    unit_type_id: undefined,
    rate_plan_id: undefined,
  });

  const [accommodationRatePlanPriceUpdateState, setAccommodationRatePlanPriceUpdateState] = useState({
    accommodationRatePlanPriceToUpdate: null,
    closeOnArrivalWarning: false,
    closeOnDepartureWarning: false,
    setLengthOfStayOpen: false,
    ratePlanName: "",
    updatedMinLos: undefined,
    updatedMaxLos: undefined,
    openEditAllPrices: false,
    editAllPrice: undefined,
    editAllRatePlan: undefined,
    editAllUnitType: undefined,
    openCloseAllOnArrival: false,
    openCloseAllOnDeparture: false,
    openSetAllLengthOfStay: false,
  });

  const tabs = [
    {
      id: "rate-plans",
      content: t("Rate plans"),
    },
    {
      id: "pricing-matrix",
      content: t("Pricing matrix"),
    },
  ];

  useEffect(() => {
    if (tab) {
      const tabIndex = tabs.findIndex(t => t.id === tab);
      if (tabIndex !== -1) {
        setSelected(tabIndex);
      }
    } else {
      // Default to first tab and update URL
      history.replace(`/admin/accommodation/rates/${tabs[0].id}`);
    }
  }, [tab]);

  useEffect(() => {
    if (selected === 1 && accommodationRatePlans?.length > 0 && filterState.rate_plan_id === undefined) {
      setFilterState(prevState => ({ ...prevState, rate_plan_id: accommodationRatePlans[0]?.id }));
    }
  }, [selected, accommodationRatePlans]);

  const selectedRatePlan = accommodationRatePlans?.find(plan => plan.id === filterState.rate_plan_id);

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (filterState?.rate_plan_id === undefined) {
      return;
    }

    void loadAccommodationRatePlanPrices();

    return () => {
      source.cancel();
    };
  }, [filterState]);

  useEffect(() => {
    void loadAccommodationUnitTypes();
    void loadAccommodationRatePlans();
  }, []);

  async function loadAccommodationRatePlans() {
    const res = await GetAccommodationRatePlans(null, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError("Error loading rate plans"));
      return;
    }

    setAccommodationRatePlans(res.data as IAccommodationRatePlan[]);
  }

  const handleTabChange = (selectedTabIndex: number) => {
    setSelected(selectedTabIndex);
    history.push(`/admin/accommodation/rates/${tabs[selectedTabIndex].id}`);
  };

  function handleOpenNewRatePlan() {
    setNewRatePlanState(prevState => ({ ...prevState, open: true }));
  }

  function setDateRangeStart(date: Date) {
    setFilterState(prevState => ({
      ...prevState,
      startingDate: date,
    }));
  }

  function setDateRangeEnd(date: Date) {
    setFilterState(prevState => ({
      ...prevState,
      endingDate: date,
    }));
  }

  function handleDateNavigation(direction: "back" | "forward") {
    const daysToShift = 7;
    const shiftValue = direction === "forward" ? daysToShift : -daysToShift;

    const newStartDate = new Date(filterState.startingDate);
    newStartDate.setDate(newStartDate.getDate() + shiftValue);

    const newEndDate = new Date(filterState.endingDate);
    newEndDate.setDate(newEndDate.getDate() + shiftValue);

    setFilterState(prevState => ({
      ...prevState,
      startingDate: newStartDate,
      endingDate: newEndDate,
    }));
  }

  async function loadAccommodationUnitTypes() {
    if (accommodationUnitTypes !== undefined) {
      setAccommodationUnitTypes(undefined);
    }
    const accommodationUnitTypesRes = await GetAccommodationUnitTypes(null, false);

    if (accommodationUnitTypesRes.status !== StatusCode.OK) {
      setAccommodationUnitTypes([]);
      return;
    }

    setAccommodationUnitTypes(accommodationUnitTypesRes.data as IAccommodationUnitType[]);
  }

  function fillDateGaps(
    prices: IAccommodationRatePlanPrice[],
    start: string,
    end: string,
  ): IAccommodationRatePlanPrice[] {
    const filled: IAccommodationRatePlanPrice[] = [];
    const day = 24 * 60 * 60 * 1000;

    const normalize = (d: string) => new Date(d).toISOString().split("T")[0];

    let dt = new Date(start);
    const endDt = new Date(end);

    const priceLookup = new Map(
      prices.map(p => {
        const key = normalize(p.date);
        return [key, { ...p, date: key }];
      }),
    );

    const template = prices[0];

    while (dt <= endDt) {
      const dateStr = normalize(dt.toISOString());

      if (priceLookup.has(dateStr)) {
        filled.push(priceLookup.get(dateStr));
      } else {
        filled.push({
          ...template,
          id: undefined,
          date: dateStr,
          price: null,
        });
      }

      dt = new Date(dt.getTime() + day);
    }

    return filled;
  }

  async function loadAccommodationRatePlanPrices() {
    interface IParams {
      rate_plan_id?: number;
      start_date: string;
      end_date: string;
      unit_type_id?: number;
    }

    const params: IParams =
      filterState?.rate_plan_id === -1
        ? {
            start_date: formatDate(filterState?.startingDate),
            end_date: formatDate(filterState?.endingDate),
          }
        : {
            rate_plan_id: Number(filterState?.rate_plan_id),
            start_date: formatDate(filterState?.startingDate),
            end_date: formatDate(filterState?.endingDate),
          };

    if (typeof filterState?.unit_type_id === "number" && filterState.unit_type_id !== -1) {
      params.unit_type_id = filterState.unit_type_id;
    }

    const res = await GetAccommodationRatePlanPrices(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError("Error loading rate plan prices"));
      return;
    }

    const rawPrices = res.data;

    // --- Group by rate_plan_id ---
    const groupedByRatePlan: Record<number, Record<number, IAccommodationRatePlanPrice[]>> = {};

    rawPrices.forEach((price: IAccommodationRatePlanPrice) => {
      const normalized = {
        ...price,
        closed_to_arrival: Boolean(price.closed_to_arrival),
        closed_to_departure: Boolean(price.closed_to_departure),
      };

      if (!groupedByRatePlan[price.rate_plan_id]) {
        groupedByRatePlan[price.rate_plan_id] = {};
      }

      if (!groupedByRatePlan[price.rate_plan_id][price.unit_type_id]) {
        groupedByRatePlan[price.rate_plan_id][price.unit_type_id] = [];
      }

      groupedByRatePlan[price.rate_plan_id][price.unit_type_id].push(normalized);
    });

    // --- convert grouped map into a structured array ---
    const groupedPrices = Object.entries(groupedByRatePlan).map(([rate_plan_id, unitsMap]) => {
      const ratePlan = accommodationRatePlans?.find(r => r.id === Number(rate_plan_id));

      const unitTypeGroups = Object.entries(unitsMap).map(([unit_type_id, prices]) => {
        const sorted = prices.sort((a, b) => a.date.localeCompare(b.date));

        return {
          unit_type_id: Number(unit_type_id),
          prices: fillDateGaps(sorted, formatDate(filterState?.startingDate), formatDate(filterState?.endingDate)),
        };
      });

      return {
        rate_plan_id: Number(rate_plan_id),
        rate_plan_name: ratePlan?.title ?? "Unknown Rate Plan",
        units: unitTypeGroups,
      };
    });

    setAccommodationRatePlanPrices(groupedPrices);
  }

  async function handleAddRatePlan() {
    const params = {
      title: newRatePlanState?.title,
      code: newRatePlanState?.code,
      public: newRatePlanState?.public,
      base_price: Number(newRatePlanState?.base_price),
    };

    const addRes = await PostAccommodationRatePlan(params, true);

    if (addRes.status !== StatusCode.OK) {
      dispatch(showError("Error adding rate plan"));
      return;
    }

    setNewRatePlanState(prevState => ({
      ...prevState,
      title: "",
      code: "",
      public: false,
      open: false,
      days_valid: "",
      base_price: "",
    }));

    void loadAccommodationRatePlans();
  }

  function handleToggleChange(event: ChangeEvent<HTMLInputElement>) {
    const id = event?.target?.id;
    const checked = event?.target?.checked;

    if (id === undefined || checked === undefined) {
      return;
    }

    setNewRatePlanState(prev => {
      return { ...prev, [id]: checked };
    });
  }

  async function handleRateChange(
    ratePlanId: number,
    unitIndex: number,
    dayIndex: number,
    newValue: number,
    ratePlanPrice: IAccommodationRatePlanPrice,
  ) {
    setSaveStatus("saving");

    const params: IPutAccommodationRatePlanPrice = {
      rate_plan_price_id: ratePlanPrice?.id,
      min_los: ratePlanPrice?.min_los,
      max_los: ratePlanPrice?.max_los,
      closed_to_arrival: ratePlanPrice?.closed_to_arrival,
      closed_to_departure: ratePlanPrice?.closed_to_departure,
      closed: ratePlanPrice?.closed,
      price: newValue,
    };

    const prevState = accommodationRatePlanPrices;

    setAccommodationRatePlanPrices(prev =>
      prev.map(plan => {
        if (plan.rate_plan_id !== ratePlanId) {
          return plan;
        }

        return {
          ...plan,
          units: plan.units.map((unit, uIndex) => {
            if (uIndex !== unitIndex) {
              return unit;
            }

            return {
              ...unit,
              prices: unit.prices.map(price => (price.id === ratePlanPrice.id ? { ...price, price: newValue } : price)),
            };
          }),
        };
      }),
    );

    const putRes = await PutAccommodationRatePlanPrice(params, false);

    if (putRes.status !== StatusCode.OK) {
      setAccommodationRatePlanPrices(prevState);
      console.error("Error updating price.");
      return;
    }

    setSaveStatus("saved");
    setTimeout(() => setSaveStatus(null), 2000);
  }

  function handleCloseArrivalChange(dayToUpdate: IAccommodationRatePlanPrice) {
    setAccommodationRatePlanPriceUpdateState(prevState => ({
      ...prevState,
      closeOnArrivalWarning: true,
      accommodationRatePlanPriceToUpdate: dayToUpdate,
      ratePlanName: accommodationRatePlans.find(rp => rp.id === dayToUpdate.rate_plan_id)?.title,
    }));
  }

  function handleCloseDepartureChange(dayToUpdate: IAccommodationRatePlanPrice) {
    setAccommodationRatePlanPriceUpdateState(prevState => ({
      ...prevState,
      closeOnDepartureWarning: true,
      accommodationRatePlanPriceToUpdate: dayToUpdate,
      ratePlanName: accommodationRatePlans.find(rp => rp.id === dayToUpdate.rate_plan_id)?.title,
    }));
  }

  function handleSetLengthOfStay(dayToUpdate: IAccommodationRatePlanPrice) {
    setAccommodationRatePlanPriceUpdateState(prevState => ({
      ...prevState,
      setLengthOfStayOpen: true,
      accommodationRatePlanPriceToUpdate: dayToUpdate,
      ratePlanName: accommodationRatePlans.find(rp => rp.id === dayToUpdate.rate_plan_id)?.title,
      updatedMaxLos: dayToUpdate.max_los,
      updatedMinLos: dayToUpdate.min_los,
    }));
  }

  function handleSetAllPrices(plan: IGroupedRatePlanPrices, unit: IUnit) {
    setAccommodationRatePlanPriceUpdateState(prevState => ({
      ...prevState,
      openEditAllPrices: true,
      editAllRatePlan: plan,
      editAllUnitType: unit,
    }));
  }

  function handleCloseAllOnArrival(plan: IGroupedRatePlanPrices, unit: IUnit) {
    setAccommodationRatePlanPriceUpdateState(prevState => ({
      ...prevState,
      openCloseAllOnArrival: true,
      editAllRatePlan: plan,
      editAllUnitType: unit,
    }));
  }

  function handleCloseAllOnDeparture(plan: IGroupedRatePlanPrices, unit: IUnit) {
    setAccommodationRatePlanPriceUpdateState(prevState => ({
      ...prevState,
      openCloseAllOnDeparture: true,
      editAllRatePlan: plan,
      editAllUnitType: unit,
    }));
  }

  function handleSetAllLengthOfStay(plan: IGroupedRatePlanPrices, unit: IUnit) {
    setAccommodationRatePlanPriceUpdateState(prevState => ({
      ...prevState,
      openSetAllLengthOfStay: true,
      editAllRatePlan: plan,
      editAllUnitType: unit,
    }));
  }

  async function updateCloseOnArrival() {
    const priceToUpdate: IAccommodationRatePlanPrice =
      accommodationRatePlanPriceUpdateState?.accommodationRatePlanPriceToUpdate;
    if (!priceToUpdate) {
      return;
    }

    const params = {
      rate_plan_price_id: priceToUpdate.id,
      min_los: priceToUpdate?.min_los,
      max_los: priceToUpdate?.max_los,
      closed: priceToUpdate.closed,
      closed_to_arrival: true,
      closed_to_departure: priceToUpdate.closed_to_departure,
      price: priceToUpdate.price,
    };

    const putRes = await PutAccommodationRatePlanPrice(params, true);

    if (putRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating closed to arrival")));
      return;
    }

    setAccommodationRatePlanPrices(prev =>
      prev.map(plan => {
        if (plan.rate_plan_id !== priceToUpdate.rate_plan_id) {
          return plan;
        }

        return {
          ...plan,
          units: plan.units.map(unit => {
            if (unit.unit_type_id !== priceToUpdate.unit_type_id) {
              return unit;
            }

            return {
              ...unit,
              prices: unit.prices.map(price =>
                price.id === priceToUpdate.id
                  ? {
                      ...price,
                      closed_to_arrival: true,
                    }
                  : price,
              ),
            };
          }),
        };
      }),
    );

    dispatch(showSuccess(t("Successfully updated closed to arrival")));

    setAccommodationRatePlanPriceUpdateState(prevState => ({
      ...prevState,
      closeOnArrivalWarning: false,
      accommodationRatePlanPriceToUpdate: undefined,
      updatedMinLos: "",
      updatedMaxLos: "",
    }));
  }

  async function updateCloseOnDeparture() {
    const priceToUpdate: IAccommodationRatePlanPrice =
      accommodationRatePlanPriceUpdateState?.accommodationRatePlanPriceToUpdate;
    if (!priceToUpdate) {
      return;
    }

    const params = {
      rate_plan_price_id: priceToUpdate.id,
      min_los: priceToUpdate?.min_los,
      max_los: priceToUpdate?.max_los,
      closed: priceToUpdate.closed,
      closed_to_arrival: priceToUpdate?.closed_to_arrival,
      closed_to_departure: true,
      price: priceToUpdate.price,
    };

    const putRes = await PutAccommodationRatePlanPrice(params, true);

    if (putRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating closed to departure")));
      return;
    }

    setAccommodationRatePlanPrices(prev =>
      prev.map(plan => {
        if (plan.rate_plan_id !== priceToUpdate.rate_plan_id) {
          return plan;
        }

        return {
          ...plan,
          units: plan.units.map(unit => {
            if (unit.unit_type_id !== priceToUpdate.unit_type_id) {
              return unit;
            }

            return {
              ...unit,
              prices: unit.prices.map(price =>
                price.id === priceToUpdate.id
                  ? {
                      ...price,
                      closed_to_departure: true,
                    }
                  : price,
              ),
            };
          }),
        };
      }),
    );

    dispatch(showSuccess(t("Successfully updated closed to departure")));

    setAccommodationRatePlanPriceUpdateState(prevState => ({
      ...prevState,
      closeOnDepartureWarning: false,
      accommodationRatePlanPriceToUpdate: undefined,
      updatedMinLos: "",
      updatedMaxLos: "",
    }));
  }

  async function updateLos() {
    const priceToUpdate = accommodationRatePlanPriceUpdateState?.accommodationRatePlanPriceToUpdate;
    if (!priceToUpdate) {
      return;
    }

    const params = {
      rate_plan_price_id: priceToUpdate.id,
      min_los: Number(accommodationRatePlanPriceUpdateState?.updatedMinLos),
      max_los: Number(accommodationRatePlanPriceUpdateState?.updatedMaxLos),
      closed: priceToUpdate.closed,
      closed_to_arrival: priceToUpdate.closed_to_arrival,
      closed_to_departure: priceToUpdate.closed_to_departure,
      price: priceToUpdate.price,
    };

    const putRes = await PutAccommodationRatePlanPrice(params, true);

    if (putRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating length of stay")));
      return;
    }

    setAccommodationRatePlanPrices(prev =>
      prev.map(plan => {
        if (plan.rate_plan_id !== priceToUpdate.rate_plan_id) {
          return plan;
        }

        return {
          ...plan,
          units: plan.units.map(unit => {
            if (unit.unit_type_id !== priceToUpdate.unit_type_id) {
              return unit;
            }

            return {
              ...unit,
              prices: unit.prices.map(price =>
                price.id === priceToUpdate.id
                  ? {
                      ...price,
                      min_los: Number(accommodationRatePlanPriceUpdateState?.updatedMinLos),
                      max_los: Number(accommodationRatePlanPriceUpdateState?.updatedMaxLos),
                    }
                  : price,
              ),
            };
          }),
        };
      }),
    );

    dispatch(showSuccess(t("Successfully updated length of stay")));

    setAccommodationRatePlanPriceUpdateState(prevState => ({
      ...prevState,
      setLengthOfStayOpen: false,
      accommodationRatePlanPriceToUpdate: undefined,
      updatedMinLos: "",
      updatedMaxLos: "",
    }));
  }

  function updatePricesByIds(ratePlanId: number, unitTypeId: number, priceIds: number[], newValue: number) {
    setAccommodationRatePlanPrices(prev =>
      prev.map(plan => {
        if (plan.rate_plan_id !== ratePlanId) {
          return plan;
        }

        return {
          ...plan,
          units: plan.units.map(unit => {
            if (unit.unit_type_id !== unitTypeId) {
              return unit;
            }

            return {
              ...unit,
              prices: unit.prices.map(price => (priceIds.includes(price.id) ? { ...price, price: newValue } : price)),
            };
          }),
        };
      }),
    );
  }

  async function updateAllPrices() {
    const allIds: number[] = accommodationRatePlanPriceUpdateState?.editAllUnitType?.prices.map((p: { id: any }) =>
      Number(p.id),
    );

    const ratePlanId: number = accommodationRatePlanPriceUpdateState.editAllRatePlan.rate_plan_id;
    const unitTypeId: number = accommodationRatePlanPriceUpdateState.editAllUnitType.unit_type_id;
    const newPrice = Number(accommodationRatePlanPriceUpdateState.editAllPrice);
    const oldState = accommodationRatePlanPrices;

    updatePricesByIds(ratePlanId, unitTypeId, allIds, newPrice);

    const params = {
      price: newPrice,
      rate_plan_price_id: ratePlanId,
      rate_plan_price_ids: allIds,
    };

    const putRes = await PutAccommodationRatePlanPrice(params, true);

    if (putRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating prices")));
      setAccommodationRatePlanPrices(oldState); // rollback UI
      return;
    }

    setAccommodationRatePlanPriceUpdateState(prev => ({
      ...prev,
      editAllPrice: "",
      editAllRatePlan: undefined,
      editAllUnitType: undefined,
      openEditAllPrices: false,
    }));
  }

  async function updateCloseAllOnArrival() {
    const allIds: number[] = accommodationRatePlanPriceUpdateState?.editAllUnitType?.prices.map((p: { id: any }) =>
      Number(p.id),
    );

    const ratePlanId: number = accommodationRatePlanPriceUpdateState.editAllRatePlan.rate_plan_id;
    const unitTypeId: number = accommodationRatePlanPriceUpdateState.editAllUnitType.unit_type_id;
    const oldState = accommodationRatePlanPrices;

    const params = {
      closed_to_arrival: true,
      rate_plan_price_id: ratePlanId,
      rate_plan_price_ids: allIds,
    };

    const putRes = await PutAccommodationRatePlanPrice(params, true);

    if (putRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating closed to arrival")));
      setAccommodationRatePlanPrices(oldState); // rollback UI
      return;
    }

    // Update frontend state
    setAccommodationRatePlanPrices(prev =>
      prev.map(plan => {
        if (plan.rate_plan_id !== ratePlanId) {
          return plan;
        }

        return {
          ...plan,
          units: plan.units.map(unit => {
            if (unit.unit_type_id !== unitTypeId) {
              return unit;
            }

            return {
              ...unit,
              prices: unit.prices.map(price =>
                allIds.includes(price.id) ? { ...price, closed_to_arrival: true } : price,
              ),
            };
          }),
        };
      }),
    );

    dispatch(showSuccess(t("Successfully updated closed to arrival")));

    setAccommodationRatePlanPriceUpdateState(prev => ({
      ...prev,
      editAllPrice: "",
      editAllRatePlan: undefined,
      editAllUnitType: undefined,
      openCloseAllOnArrival: false,
    }));
  }

  async function updateCloseAllOnDeparture() {
    const allIds: number[] = accommodationRatePlanPriceUpdateState?.editAllUnitType?.prices.map((p: { id: any }) =>
      Number(p.id),
    );

    const ratePlanId: number = accommodationRatePlanPriceUpdateState.editAllRatePlan.rate_plan_id;
    const unitTypeId: number = accommodationRatePlanPriceUpdateState.editAllUnitType.unit_type_id;
    const oldState = accommodationRatePlanPrices;

    const params = {
      closed_to_departure: true,
      rate_plan_price_id: ratePlanId,
      rate_plan_price_ids: allIds,
    };

    const putRes = await PutAccommodationRatePlanPrice(params, true);

    if (putRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating closed to departure")));
      setAccommodationRatePlanPrices(oldState); // rollback UI
      return;
    }

    // Update frontend state
    setAccommodationRatePlanPrices(prev =>
      prev.map(plan => {
        if (plan.rate_plan_id !== ratePlanId) {
          return plan;
        }

        return {
          ...plan,
          units: plan.units.map(unit => {
            if (unit.unit_type_id !== unitTypeId) {
              return unit;
            }

            return {
              ...unit,
              prices: unit.prices.map(price =>
                allIds.includes(price.id) ? { ...price, closed_to_departure: true } : price,
              ),
            };
          }),
        };
      }),
    );

    dispatch(showSuccess(t("Successfully updated closed to departure")));

    setAccommodationRatePlanPriceUpdateState(prev => ({
      ...prev,
      editAllPrice: "",
      editAllRatePlan: undefined,
      editAllUnitType: undefined,
      openCloseAllOnDeparture: false,
    }));
  }

  async function updateAllLos() {
    const allIds: number[] = accommodationRatePlanPriceUpdateState?.editAllUnitType?.prices.map((p: { id: any }) =>
      Number(p.id),
    );

    const ratePlanId: number = accommodationRatePlanPriceUpdateState.editAllRatePlan.rate_plan_id;
    const oldState = accommodationRatePlanPrices;

    const params = {
      rate_plan_price_id: ratePlanId,
      rate_plan_price_ids: allIds,
      min_los: Number(accommodationRatePlanPriceUpdateState?.updatedMinLos),
      max_los:
        accommodationRatePlanPriceUpdateState?.updatedMaxLos?.length > 0
          ? Number(accommodationRatePlanPriceUpdateState?.updatedMaxLos)
          : null,
    };

    const putRes = await PutAccommodationRatePlanPrice(params, true);

    if (putRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating length of stay")));
      setAccommodationRatePlanPrices(oldState); // rollback UI
      return;
    }

    setAccommodationRatePlanPrices(prev =>
      prev.map(plan => {
        if (plan.rate_plan_id !== ratePlanId) {
          return plan;
        }

        return {
          ...plan,
          units: plan.units.map(unit => {
            if (unit.unit_type_id !== accommodationRatePlanPriceUpdateState.editAllUnitType.unit_type_id) {
              return unit;
            }

            return {
              ...unit,
              prices: unit.prices.map(price =>
                allIds.includes(price.id)
                  ? {
                      ...price,
                      min_los: Number(accommodationRatePlanPriceUpdateState?.updatedMinLos),
                      max_los: Number(accommodationRatePlanPriceUpdateState?.updatedMaxLos),
                    }
                  : price,
              ),
            };
          }),
        };
      }),
    );

    dispatch(showSuccess(t("Successfully updated length of stay")));

    setAccommodationRatePlanPriceUpdateState(prevState => ({
      ...prevState,
      editAllRatePlan: undefined,
      editAllUnitType: undefined,
      openSetAllLengthOfStay: false,
      updatedMinLos: "",
      updatedMaxLos: "",
    }));
  }

  const primaryAction = {
    content: t("Add new rate plan"),
    action: handleOpenNewRatePlan,
  };

  const autoSave: IActionDetails | null =
    saveStatus === "saving"
      ? { icon: "spinner", iconProps: { spin: true, style: { fontSize: "16px" } }, content: "Saving..." }
      : saveStatus === "saved"
      ? { icon: "circle-check", content: "Saved", iconProps: { style: { fontSize: "16px" } } }
      : null;

  return (
    <Page
      title="Rate plans"
      subtitle="Manage and keep track of your rate plans in one place."
      primaryAction={primaryAction}
      breadcrumbs={[
        { label: t("Accommodations"), url: "/admin/accommodation/calendar" },
        { label: t("Rates & availability"), url: "/admin/accommodation/rates" },
        { label: t("Rate plans"), url: "/admin/accommodation/rates" },
      ]}
      actionDetails={autoSave}
    >
      <Card>
        <div className="property-rate-plans-tabs-container">
          <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} />
        </div>

        {selected === 0 ? (
          <>
            <div style={{ marginTop: "24px" }}>
              <DataTable
                noRoundedTop
                columns={[
                  { label: t("Plan name (Public)"), width: "25%" },
                  { label: t("Code"), width: "20%" },
                  { label: t("Public"), width: "20%" },
                  { label: t("Base rate (per night)"), width: "20%" },
                  { label: "", width: "15%" },
                ]}
                loading={accommodationRatePlans === undefined}
                footer={{
                  tableLimit: filterState?.limit,
                  tableOffset: filterState?.offset,
                  handleTableOffset: direction =>
                    setFilterState(prev => ({
                      ...prev,
                      offset: direction === "prev" ? prev.offset - prev.limit : prev.offset + prev.limit,
                    })),
                  disableNextOffset:
                    !accommodationRatePlans || !(accommodationRatePlans?.length === filterState?.limit),
                }}
                className="rate-plans-table"
              >
                {accommodationRatePlans?.map((ratePlan: IAccommodationRatePlan, index: number) => {
                  return (
                    <tr
                      key={index}
                      className="clickable"
                      onClick={() => history.push("/admin/accommodation/rates/details/" + String(ratePlan?.id))}
                    >
                      <td>{ratePlan.title}</td>

                      <td>{ratePlan.code}</td>

                      <td>{ratePlan.public ? "Yes" : "No"}</td>

                      <td>{ratePlan.base_price}</td>

                      <td className="text-center">
                        <FontAwesomeIcon
                          icon={faPencil}
                          onClick={() => history.push("/admin/accommodation/rates/details/" + String(ratePlan?.id))}
                          className="edit-icon"
                        />
                      </td>
                    </tr>
                  );
                })}
              </DataTable>
            </div>
          </>
        ) : null}

        {selected === 1 ? (
          <div>
            <div className="m-4">
              <FormLayout>
                <FormLayout.Group>
                  <DatePickerInput
                    months={1}
                    position="left"
                    showQuickOptions
                    startingDate={filterState?.startingDate}
                    setStartingDate={setDateRangeStart}
                    endingDate={filterState?.endingDate}
                    setEndingDate={setDateRangeEnd}
                    maxDayRange={7}
                  />

                  <div>
                    <Select
                      onChange={(value: number) => setFilterState(prev => ({ ...prev, unit_type_id: Number(value) }))}
                      defaultValue={-1}
                      placeholder="Select unit type"
                    >
                      <Select.Option value={-1} name={"All"}>
                        {t("All")}
                      </Select.Option>

                      {accommodationUnitTypes?.map((unitType: IAccommodationUnitType) => (
                        <Select.Option key={unitType.id} value={unitType.id} name={unitType.title}>
                          {unitType.title}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <Select
                      onChange={(value: number) => setFilterState(prev => ({ ...prev, rate_plan_id: Number(value) }))}
                      defaultValue={-1}
                      placeholder="Select rate plan"
                    >
                      <Select.Option value={-1} name={"All"}>
                        {t("All")}
                      </Select.Option>
                      {accommodationRatePlans?.map((unitType: IAccommodationRatePlan) => (
                        <Select.Option key={unitType.id} value={unitType.id} name={unitType.title}>
                          {unitType.title}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </FormLayout.Group>
              </FormLayout>
            </div>

            <RatePlanTable
              ratePlans={accommodationRatePlanPrices}
              onRateChange={handleRateChange}
              handleCloseArrivalChange={handleCloseArrivalChange}
              handleCloseDepartureChange={handleCloseDepartureChange}
              handleSetLengthOfStay={handleSetLengthOfStay}
              handleOpenEditAllPrices={handleSetAllPrices}
              handleOpenCloseAllOnArrival={handleCloseAllOnArrival}
              handleOpenCloseAllOnDeparture={handleCloseAllOnDeparture}
              handleOpenSetAllLengthOfStay={handleSetAllLengthOfStay}
              startingDate={filterState?.startingDate}
              endingDate={filterState?.endingDate}
              onDateNavigation={handleDateNavigation}
              currentRatePlan={selectedRatePlan}
              unitTypes={accommodationUnitTypes}
            />
          </div>
        ) : null}
      </Card>

      {/* Add New Rate Plan Modal */}
      <Sheet
        open={newRatePlanState?.open}
        title={t("Add new rate plan")}
        supportText="Create a new rate plan to base your room sales on."
        size="small"
        onCancel={() =>
          setNewRatePlanState(prevState => ({
            ...prevState,
            open: false,
            code: "",
            title: "",
            public: false,
            days_valid: "",
            base_price: "",
          }))
        }
        cancelText={t("Cancel")}
        backDropCancel
        closable
        okText={t("Add rate plan")}
        onOk={handleAddRatePlan}
      >
        <div>
          <FormLayout>
            <FormLayout.Group>
              <Input
                label={t("Name of rate plan")}
                id={"title"}
                value={newRatePlanState?.title}
                onChange={e => setNewRatePlanState(prevState => ({ ...prevState, title: e.target.value }))}
                placeholder="Enter rate plan name"
                autoFocus
              />

              <Input
                label={t("Rate plan code")}
                id={"code"}
                value={newRatePlanState?.code}
                onChange={e => setNewRatePlanState(prevState => ({ ...prevState, code: e.target.value }))}
                placeholder="Enter rate plan code"
                autoFocus
              />
            </FormLayout.Group>
            <FormLayout.Group>
              {/* <Input
                label={t("Duration (Days valid)")}
                id={"days_valid"}
                value={newRatePlanState?.days_valid}
                onChange={e => setNewRatePlanState(prevState => ({ ...prevState, days_valid: e.target.value }))}
                placeholder="Min 1"
                autoFocus
                type={"number"}
              /> */}

              <Input
                label={t("Base Price")}
                id={"base_price"}
                value={newRatePlanState?.base_price}
                onChange={e => setNewRatePlanState(prevState => ({ ...prevState, base_price: e.target.value }))}
                placeholder="Min 1"
                autoFocus
                type={"number"}
              />

              <div className="text-center">
                <Toggle
                  label={t("Public")}
                  id="public"
                  checked={newRatePlanState?.public}
                  onChange={handleToggleChange}
                />
              </div>
            </FormLayout.Group>
            <FormLayout.Group></FormLayout.Group>
          </FormLayout>
        </div>
      </Sheet>

      {/* Warning Modals */}
      <Portal isMounted={accommodationRatePlanPriceUpdateState?.closeOnArrivalWarning}>
        <Popup
          open={accommodationRatePlanPriceUpdateState?.closeOnArrivalWarning}
          title={t("Close on Arrival")}
          description={t(
            "Are you sure you want to close to arrival for " +
              accommodationRatePlanPriceUpdateState?.ratePlanName +
              " on " +
              accommodationRatePlanPriceUpdateState?.accommodationRatePlanPriceToUpdate?.date +
              "?",
          )}
          onOk={updateCloseOnArrival}
          okText={t("Close")}
          onCancel={() =>
            setAccommodationRatePlanPriceUpdateState(prevState => ({
              ...prevState,
              closeOnArrivalWarning: false,
              accommodationRatePlanPriceToUpdate: null,
            }))
          }
          type="warning"
        />
      </Portal>

      <Portal isMounted={accommodationRatePlanPriceUpdateState?.closeOnDepartureWarning}>
        <Popup
          open={accommodationRatePlanPriceUpdateState?.closeOnDepartureWarning}
          title={t("Close on Departure")}
          description={t(
            "Are you sure you want to close to departure for " +
              accommodationRatePlanPriceUpdateState?.ratePlanName +
              " on " +
              accommodationRatePlanPriceUpdateState?.accommodationRatePlanPriceToUpdate?.date +
              "?",
          )}
          onOk={updateCloseOnDeparture}
          okText={t("Close")}
          onCancel={() =>
            setAccommodationRatePlanPriceUpdateState(prevState => ({
              ...prevState,
              closeOnDepartureWarning: false,
              accommodationRatePlanPriceToUpdate: null,
            }))
          }
          type="warning"
        />
      </Portal>

      <Sheet
        open={accommodationRatePlanPriceUpdateState?.setLengthOfStayOpen}
        title={t("Set length of stay")}
        supportText={
          "Add length of stay restrictions for your " +
          accommodationRatePlanPriceUpdateState?.ratePlanName +
          " on " +
          accommodationRatePlanPriceUpdateState?.accommodationRatePlanPriceToUpdate?.date
        }
        size="small"
        onCancel={() =>
          setAccommodationRatePlanPriceUpdateState(prevState => ({
            ...prevState,
            accommodationRatePlanPriceToUpdate: null,
            setLengthOfStayOpen: false,
            updatedMinLos: "",
            updatedMaxLos: "",
          }))
        }
        cancelText={t("Cancel")}
        backDropCancel
        closable
        okText={t("Done")}
        onOk={updateLos}
      >
        <div>
          <FormLayout>
            <FormLayout.Group>
              <Input
                label={t("Min. days")}
                id={"min_los"}
                value={accommodationRatePlanPriceUpdateState?.updatedMinLos}
                onChange={e =>
                  setAccommodationRatePlanPriceUpdateState(prevState => ({
                    ...prevState,
                    updatedMinLos: e.target.value,
                  }))
                }
                placeholder="1"
                autoFocus
                type={"number"}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                label={t("Max. days (optional)")}
                id={"max_los"}
                value={accommodationRatePlanPriceUpdateState?.updatedMaxLos}
                onChange={e =>
                  setAccommodationRatePlanPriceUpdateState(prevState => ({
                    ...prevState,
                    updatedMaxLos: e.target.value,
                  }))
                }
                placeholder="0"
                autoFocus
                type={"number"}
                helpText={'Enter "0" if you do not require a maximum length of stay.'}
              />
            </FormLayout.Group>
          </FormLayout>
        </div>
      </Sheet>

      <Sheet
        open={accommodationRatePlanPriceUpdateState?.openEditAllPrices}
        title={t("Update Prices")}
        supportText={
          "Update prices for dates between " +
          formatDate(filterState?.startingDate) +
          " and " +
          formatDate(filterState?.endingDate)
        }
        size="small"
        onCancel={() =>
          setAccommodationRatePlanPriceUpdateState(prevState => ({
            ...prevState,
            openEditAllPrices: false,
            editAllRatePlan: null,
            editAllUnitType: null,
          }))
        }
        cancelText={t("Cancel")}
        backDropCancel
        closable
        okText={t("Update")}
        onOk={updateAllPrices}
      >
        <div>
          <FormLayout>
            <FormLayout.Group>
              <Input
                label={t("New price")}
                id={"editAllPrice"}
                value={accommodationRatePlanPriceUpdateState?.editAllPrice}
                onChange={e =>
                  setAccommodationRatePlanPriceUpdateState(prevState => ({
                    ...prevState,
                    editAllPrice: e.target.value,
                  }))
                }
                placeholder="0"
                autoFocus
                type={"number"}
              />
            </FormLayout.Group>
          </FormLayout>
        </div>
      </Sheet>

      <Portal isMounted={accommodationRatePlanPriceUpdateState?.openCloseAllOnArrival}>
        <Popup
          open={accommodationRatePlanPriceUpdateState?.openCloseAllOnArrival}
          title={t("Close on Arrival")}
          description={t(
            "Are you sure you want to close arrival for dates between " +
              formatDate(filterState?.startingDate) +
              " and " +
              formatDate(filterState?.endingDate),
          )}
          onOk={updateCloseAllOnArrival}
          okText={t("Close")}
          onCancel={() =>
            setAccommodationRatePlanPriceUpdateState(prevState => ({
              ...prevState,
              openCloseAllOnArrival: false,
              editAllRatePlan: null,
              editAllUnitType: null,
            }))
          }
          type="warning"
        />
      </Portal>

      <Portal isMounted={accommodationRatePlanPriceUpdateState?.openCloseAllOnDeparture}>
        <Popup
          open={accommodationRatePlanPriceUpdateState?.openCloseAllOnDeparture}
          title={t("Close on Departure")}
          description={t(
            "Are you sure you want to close departure for dates between " +
              formatDate(filterState?.startingDate) +
              " and " +
              formatDate(filterState?.endingDate),
          )}
          onOk={updateCloseAllOnDeparture}
          okText={t("Close")}
          onCancel={() =>
            setAccommodationRatePlanPriceUpdateState(prevState => ({
              ...prevState,
              openCloseAllOnDeparture: false,
              editAllRatePlan: null,
              editAllUnitType: null,
            }))
          }
          type="warning"
        />
      </Portal>

      <Portal isMounted={accommodationRatePlanPriceUpdateState?.openSetAllLengthOfStay}>
        <Sheet
          open={accommodationRatePlanPriceUpdateState?.openSetAllLengthOfStay}
          title={t("Set length of stay")}
          supportText={
            "Add length of stay restrictions for your " +
            accommodationRatePlanPriceUpdateState?.ratePlanName +
            " between " +
            formatDate(filterState?.startingDate) +
            " and " +
            formatDate(filterState?.endingDate)
          }
          size="small"
          onCancel={() =>
            setAccommodationRatePlanPriceUpdateState(prevState => ({
              ...prevState,
              openSetAllLengthOfStay: false,
              editAllRatePlan: null,
              editAllUnitType: null,
              updatedMinLos: "",
              updatedMaxLos: "",
            }))
          }
          cancelText={t("Cancel")}
          backDropCancel
          closable
          okText={t("Done")}
          onOk={updateAllLos}
          okDisabled={accommodationRatePlanPriceUpdateState?.updatedMinLos?.length === 0}
        >
          <div>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  label={t("Min. days")}
                  id={"min_los"}
                  value={accommodationRatePlanPriceUpdateState?.updatedMinLos}
                  onChange={e =>
                    setAccommodationRatePlanPriceUpdateState(prevState => ({
                      ...prevState,
                      updatedMinLos: e.target.value,
                    }))
                  }
                  placeholder="1"
                  autoFocus
                  type={"number"}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <Input
                  label={t("Max. days (optional)")}
                  id={"max_los"}
                  value={accommodationRatePlanPriceUpdateState?.updatedMaxLos}
                  onChange={e =>
                    setAccommodationRatePlanPriceUpdateState(prevState => ({
                      ...prevState,
                      updatedMaxLos: e.target.value,
                    }))
                  }
                  placeholder="0"
                  type={"number"}
                  helpText={'Enter "0" if you do not require a maximum length of stay.'}
                />
              </FormLayout.Group>
            </FormLayout>
          </div>
        </Sheet>
      </Portal>
    </Page>
  );
}
